import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import EdpiesLogo from '@/components/EdpiesLogo';
import { 
  GraduationCap, 
  Users, 
  Clock, 
  DollarSign, 
  Heart, 
  Target,
  CheckCircle2,
  ArrowRight,
  Star,
  BookOpen,
  Sparkles,
  TrendingUp
} from 'lucide-react';

const TutorLandingPage = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: Heart,
      title: 'Make Real Impact',
      description: 'Make a real, measurable impact on students\' learning journeys and help shape brighter futures.'
    },
    {
      icon: Users,
      title: 'Join Our Community',
      description: 'Be part of a growing, purpose-driven education community of passionate educators.'
    },
    {
      icon: Clock,
      title: 'Flexible Schedule',
      description: 'Teach flexibly, on your own terms. Set your own hours and availability.'
    },
    {
      icon: DollarSign,
      title: 'Earn Extra Income',
      description: 'Earn additional income while doing what you love - teaching and inspiring students.'
    }
  ];

  const features = [
    'Create your professional tutor profile',
    'Set your own hourly rates',
    'Connect with students across UK exam levels',
    'Receive direct inquiries from parents',
    'Access to teaching resources and question banks',
    'Build your reputation with reviews'
  ];

  const stats = [
    { value: '1000+', label: 'Students' },
    { value: '50+', label: 'Active Tutors' },
    { value: '6', label: 'Exam Stages' },
    { value: '4.8', label: 'Avg Rating' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-emerald-950">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <motion.div 
              className="flex items-center gap-2 cursor-pointer"
              whileHover={{ scale: 1.05 }}
              onClick={() => navigate('/')}
            >
              <EdpiesLogo size="sm" />
              <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
                Edpies
              </span>
            </motion.div>
            
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="text-slate-300 hover:text-white"
              >
                Home
              </Button>
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
              >
                Join Now
              </Button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div 
            className="absolute bottom-20 right-10 w-96 h-96 bg-green-500/20 rounded-full blur-3xl"
            animate={{ 
              scale: [1.2, 1, 1.2],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ duration: 10, repeat: Infinity }}
          />
        </div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium mb-6 border border-emerald-500/30">
              <Sparkles className="inline h-4 w-4 mr-2" />
              Now Accepting Tutor Applications
            </span>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>
              Teach. Inspire.{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">
                Earn.
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-8 leading-relaxed">
              Join Edpies as a Tutor or Educator. Empower community kids, transform learning experiences, 
              and unlock a flexible side-earning opportunity.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg"
                  onClick={() => navigate('/auth')}
                  className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-lg px-8 py-6"
                >
                  <GraduationCap className="mr-2 h-5 w-5" />
                  Register as a Tutor
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {stats.map((stat, idx) => (
              <div key={idx} className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
                <p className="text-3xl md:text-4xl font-bold text-emerald-400">{stat.value}</p>
                <p className="text-slate-400 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              At Edpies, We Believe Great Educators{' '}
              <span className="text-emerald-400">Change Lives</span>
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed">
              We're inviting passionate tutors and educators to collaborate with us in shaping brighter futures 
              for community children. Whether you're an experienced tutor or an educator looking to extend your reach, 
              Edpies gives you the platform, tools, and support to teach smarter and connect deeper with students.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Join Edpies?
            </h2>
            <p className="text-slate-400 text-lg">By joining Edpies, you'll have the opportunity to:</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
              >
                <Card className="bg-slate-800/50 border-slate-700/50 hover:border-emerald-500/50 transition-all duration-300 h-full">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="bg-emerald-500/20 p-3 rounded-xl">
                        <benefit.icon className="h-8 w-8 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                        <p className="text-slate-400">{benefit.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-emerald-900/30 to-green-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Everything You Need to <span className="text-emerald-400">Succeed</span>
              </h2>
              <p className="text-slate-300 mb-8">
                Our platform provides all the tools and support you need to build a successful tutoring practice.
              </p>
              <ul className="space-y-4">
                {features.map((feature, idx) => (
                  <motion.li 
                    key={idx}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-2xl p-8 border border-emerald-500/30">
                <div className="bg-slate-800 rounded-xl p-6 shadow-2xl">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <GraduationCap className="h-8 w-8 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold">Sample Tutor Profile</h4>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        ))}
                        <span className="text-slate-400 text-sm ml-2">5.0 (24 reviews)</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-slate-300">
                      <BookOpen className="h-4 w-4 text-emerald-400" />
                      <span>Maths, English, 11+ Prep</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Clock className="h-4 w-4 text-emerald-400" />
                      <span>8 years experience</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <TrendingUp className="h-4 w-4 text-emerald-400" />
                      <span>£45/hour</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-emerald-600 to-green-600 rounded-3xl p-12 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Together, Let's Transform Learning
              </h2>
              <p className="text-xl text-white/90 mb-8">
                One child at a time.
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg"
                  onClick={() => navigate('/auth')}
                  className="bg-white text-emerald-600 hover:bg-slate-100 text-lg px-10 py-6 font-bold"
                >
                  Start Your Journey Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <EdpiesLogo size="sm" />
            <span className="text-slate-400">© 2025 Edpies. All rights reserved.</span>
          </div>
          <div className="flex gap-6">
            <Button variant="link" onClick={() => navigate('/about')} className="text-slate-400 hover:text-white">
              About
            </Button>
            <Button variant="link" onClick={() => navigate('/contact')} className="text-slate-400 hover:text-white">
              Contact
            </Button>
            <Button variant="link" onClick={() => navigate('/faq')} className="text-slate-400 hover:text-white">
              FAQ
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TutorLandingPage;
